/**
 * WHAT: Centralized Axios-based API client with interceptors, domain-organized endpoints, and retry logic.
 * WHY:  Every DAMA component talks to the Django backend through this single service layer, which
 *       automatically attaches auth tokens and the X-Username header required for RBAC, handles
 *       401 redirects, and provides a consistent error-handling contract across the entire frontend.
 */

import axios from 'axios';

/**
 * Centralized API Service Layer
 *
 * Benefits:
 * - Single source of truth for API configuration
 * - Automatic token management
 * - Consistent error handling
 * - Request/Response interceptors for logging and debugging
 * - Easy to mock for testing
 * - Automatic retry logic for failed requests
 */

// Resolve the API base URL with the same precedence as config/api.config.js and AuthContext.js:
//   1. RUNTIME window.__DAMA_CONFIG__.apiBaseUrl (public/config.js — editable per install, COTS path)
//   2. build-time REACT_APP_API_BASE_URL
//   3. '' = same-origin (single-host installs where Django/WhiteNoise serve UI+API together)
// NOTE: '' is intentional and must use ?? / explicit checks, not ||, because '' is falsy — a `||`
// chain would skip a deliberately-empty runtime value and wrongly fall through to a hardcoded host.
const RUNTIME_CONFIG = (typeof window !== 'undefined' && window.__DAMA_CONFIG__) || {};
const API_BASE_URL =
  (RUNTIME_CONFIG.apiBaseUrl !== undefined && RUNTIME_CONFIG.apiBaseUrl !== null)
    ? RUNTIME_CONFIG.apiBaseUrl
    : (process.env.REACT_APP_API_BASE_URL !== undefined && process.env.REACT_APP_API_BASE_URL !== null)
      ? process.env.REACT_APP_API_BASE_URL
      : '';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  // Send the server-side session cookie on every request. Identity is now derived from the
  // session server-side (not the X-Username header), so the cookie must ride along.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - Add authentication token and username to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }

    // Add X-Username header for permission checks
    const userStr = localStorage.getItem('dama_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.username) {
          config.headers['X-Username'] = user.username;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Log request in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request: ${config.method.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle common errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response;
  },
  (error) => {
    // Handle specific HTTP error codes
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - Token expired or invalid
          console.error('⛔ Authentication failed - redirecting to login');
          localStorage.removeItem('token');
          localStorage.removeItem('dama_user');

          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;

        case 403:
          // Forbidden - User doesn't have permission
          console.error('⛔ Permission denied:', data);
          break;

        case 404:
          // Not Found
          console.error('❌ Resource not found:', error.config.url);
          break;

        case 422:
          // Validation Error
          console.error('⚠️ Validation error:', data);
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error('🔥 Server error:', status, data);
          break;

        default:
          console.error('❌ API Error:', status, data);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('📡 Network error - no response received:', error.message);
    } else {
      // Something else happened
      console.error('❌ Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * WHAT: Domain-organized API service object exposing CRUD methods for every DAMA resource.
 * WHY:  Components import `api.campaigns.create(...)` etc. instead of constructing URLs
 *       themselves, keeping endpoint knowledge centralized and making API changes a one-line fix.
 */
export const api = {
  // ==================== CAMPAIGNS ====================
  campaigns: {
    getAll: () => apiClient.get('/campaigns/'),
    getById: (campaignId) => apiClient.get(`/campaigns/${campaignId}/`),
    create: (campaignData) => apiClient.post('/campaigns/', campaignData),
    update: (campaignId, campaignData) => apiClient.put(`/campaigns/${campaignId}/`, campaignData),
    delete: (campaignId) => apiClient.delete(`/campaigns/${campaignId}/`),
    getStatus: (campaignId) => apiClient.get(`/campaigns/${campaignId}/status/`),
  },

  // ==================== ENTERPRISE USERS ====================
  enterpriseUsers: {
    getAll: (url = '/api/enterprise-users/') => apiClient.get(url),
    getById: (userId) => apiClient.get(`/api/enterprise-users/${userId}/`),
    getRefreshInfo: () => apiClient.get('/api/enterprise-users/refresh-info/'),
    refresh: () => apiClient.post('/api/enterprise-users/refresh/'),
    getUsers: (filter = 'active') => apiClient.get(`/api/databases/enterprise-users/?filter=${filter}`),

    // Azure AD Sync
    azureAdSync: () => apiClient.post('/api/databases/enterprise-users/azure-ad/sync/'),
    getAzureAdStatus: () => apiClient.get('/api/databases/enterprise-users/azure-ad/status/'),
    testAzureAdConnection: () => apiClient.get('/api/databases/enterprise-users/azure-ad/test/'),

    // Azure AD Security Groups
    getAzureAdGroups: () => apiClient.get('/api/databases/enterprise-users/azure-ad/groups/'),
    getAzureAdGroupMembers: (groupId) => apiClient.get(`/api/databases/enterprise-users/azure-ad/groups/${groupId}/members/`),
    syncAzureAdGroups: () => apiClient.post('/api/databases/enterprise-users/azure-ad/groups/sync/'),

    // Change History
    getChangeHistory: (params) => apiClient.get('/api/enterprise-users/changes/', { params }),
    getUserTimeline: (userId) => apiClient.get(`/api/enterprise-users/${userId}/timeline/`),
    getChangeStatistics: (params) => apiClient.get('/api/enterprise-users/changes/statistics/', { params }),
    getHighRiskChanges: (params) => apiClient.get('/api/enterprise-users/changes/high-risk/', { params }),
    getDepartmentMovement: (params) => apiClient.get('/api/enterprise-users/changes/department-movement/', { params }),
    getPromotionsDemotions: (params) => apiClient.get('/api/enterprise-users/changes/promotions-demotions/', { params }),
  },

  // ==================== ATTESTATIONS ====================
  attestations: {
    prepare: (campaignId, filterInScope = true) =>
      apiClient.get(`/attestation/prepare/`, {
        params: {
          campaign_id: campaignId,
          filter_in_scope: filterInScope,
          _t: Date.now() // Cache buster
        }
      }),
    submit: (attestationData) => apiClient.post('/attestation/submit/', attestationData),
    update: (attestationId, data) => apiClient.put(`/attestation/${attestationId}/`, data),
    bulkUpdate: (attestationData) => apiClient.post('/attestation/bulk-update/', attestationData),
    getByManager: (managerId, campaignId) =>
      apiClient.get(`/attestation/manager/${managerId}/`, { params: { campaign_id: campaignId } }),
  },

  // ==================== APPLICATIONS ====================
  applications: {
    getAll: () => apiClient.get('/api/databases/applications/'),
    getById: (appId) => apiClient.get(`/api/databases/applications/${appId}/`),
    create: (appData) => apiClient.post('/api/databases/applications/create/', appData),
    update: (appId, appData) => apiClient.put(`/api/databases/applications/${appId}/update/`, appData),
    delete: (appId) => apiClient.delete(`/api/databases/applications/${appId}/`),
  },

  // ==================== DATABASES ====================
  databases: {
    getAll: () => apiClient.get('/database-list/'),
    getById: (dbId) => apiClient.get(`/database-list/${dbId}/`),

    // Database Connections
    getConnections: (params) => apiClient.get('/api/databases/connections/', { params }),
    getAggregatedConnections: (params) => apiClient.get('/api/databases/connections/aggregated/', { params }),
    getConnectionById: (connId) => apiClient.get(`/api/databases/connections/${connId}/`),
    getConnectionStats: () => apiClient.get('/api/databases/connections/stats/'),
    createConnection: (connectionData) => apiClient.post('/api/databases/connections/create/', connectionData),
    updateConnection: (connId, connectionData) => apiClient.put(`/api/databases/connections/${connId}/update/`, connectionData),
    deleteConnection: (connId) => apiClient.delete(`/api/databases/connections/${connId}/delete/`),
    testConnection: (connId) => apiClient.post(`/api/databases/connections/${connId}/test/`),

    // Database Mappings
    getMappings: () => apiClient.get('/api/databases/database-mappings/'),
    getAppDatabaseMappings: () => apiClient.get('/api/databases/app-database-mappings/'),
    createMapping: (mappingData) => apiClient.post('/api/databases/database-mappings/', mappingData),
    updateMapping: (mappingId, mappingData) => apiClient.put(`/api/databases/database-mappings/${mappingId}/`, mappingData),
    deleteMapping: (mappingId) => apiClient.delete(`/api/databases/database-mappings/${mappingId}/`),

    // MSSQL Instance Databases
    getMssqlInstanceDatabases: (params) => apiClient.get('/api/databases/mssql/instance-databases/', { params }),
    getMssqlConnections: () => apiClient.get('/api/databases/mssql/connections/'),

    // Oracle-specific Connections (source_oracle_connections table)
    getOracleConnections: (params) => apiClient.get('/api/databases/oracle/connections/', { params }),
    getOracleConnectionById: (connId) => apiClient.get(`/api/databases/oracle/connections/${connId}/`),
    createOracleConnection: (connectionData) => apiClient.post('/api/databases/oracle/connections/create/', connectionData),
    updateOracleConnection: (connId, connectionData) => apiClient.put(`/api/databases/oracle/connections/${connId}/update/`, connectionData),
    deleteOracleConnection: (connId) => apiClient.delete(`/api/databases/oracle/connections/${connId}/delete/`),
    testOracleConnection: (connId) => apiClient.post(`/api/databases/oracle/connections/${connId}/test/`),

    // MSSQL Source Connections (source_mssql_connections table)
    getMssqlSourceConnections: (params) => apiClient.get('/api/databases/mssql/source-connections/', { params }),
    getMssqlSourceConnectionById: (connId) => apiClient.get(`/api/databases/mssql/source-connections/${connId}/`),
    createMssqlSourceConnection: (connectionData) => apiClient.post('/api/databases/mssql/source-connections/create/', connectionData),
    updateMssqlSourceConnection: (connId, connectionData) => apiClient.put(`/api/databases/mssql/source-connections/${connId}/update/`, connectionData),
    deleteMssqlSourceConnection: (connId) => apiClient.delete(`/api/databases/mssql/source-connections/${connId}/delete/`),
    testMssqlSourceConnection: (connId) => apiClient.post(`/api/databases/mssql/source-connections/${connId}/test/`),

    // MySQL Source Connections (source_mysql_connections table)
    getMysqlSourceConnections: (params) => apiClient.get('/api/databases/mysql/source-connections/', { params }),
    getMysqlSourceConnectionById: (connId) => apiClient.get(`/api/databases/mysql/source-connections/${connId}/`),
    createMysqlSourceConnection: (connectionData) => apiClient.post('/api/databases/mysql/source-connections/create/', connectionData),
    updateMysqlSourceConnection: (connId, connectionData) => apiClient.put(`/api/databases/mysql/source-connections/${connId}/update/`, connectionData),
    deleteMysqlSourceConnection: (connId) => apiClient.delete(`/api/databases/mysql/source-connections/${connId}/delete/`),
    testMysqlSourceConnection: (connId) => apiClient.post(`/api/databases/mysql/source-connections/${connId}/test/`),

    // Cassandra Source Connections (source_cassandra_connections table)
    getCassandraSourceConnections: (params) => apiClient.get('/api/databases/cassandra/source-connections/', { params }),
    getCassandraSourceConnectionById: (connId) => apiClient.get(`/api/databases/cassandra/source-connections/${connId}/`),
    createCassandraSourceConnection: (connectionData) => apiClient.post('/api/databases/cassandra/source-connections/create/', connectionData),
    updateCassandraSourceConnection: (connId, connectionData) => apiClient.put(`/api/databases/cassandra/source-connections/${connId}/update/`, connectionData),
    deleteCassandraSourceConnection: (connId) => apiClient.delete(`/api/databases/cassandra/source-connections/${connId}/delete/`),
    testCassandraSourceConnection: (connId) => apiClient.post(`/api/databases/cassandra/source-connections/${connId}/test/`),

    // PostgreSQL Source Connections (source_postgresql_connections table)
    getPostgresqlSourceConnections: (params) => apiClient.get('/api/databases/postgresql/source-connections/', { params }),
    getPostgresqlSourceConnectionById: (connId) => apiClient.get(`/api/databases/postgresql/source-connections/${connId}/`),
    createPostgresqlSourceConnection: (connectionData) => apiClient.post('/api/databases/postgresql/source-connections/create/', connectionData),
    updatePostgresqlSourceConnection: (connId, connectionData) => apiClient.put(`/api/databases/postgresql/source-connections/${connId}/update/`, connectionData),
    deletePostgresqlSourceConnection: (connId) => apiClient.delete(`/api/databases/postgresql/source-connections/${connId}/delete/`),
    testPostgresqlSourceConnection: (connId) => apiClient.post(`/api/databases/postgresql/source-connections/${connId}/test/`),
  },

  // ==================== DATABASE TYPES ====================
  databaseTypes: {
    getAll: () => apiClient.get('/database-types/'),
    create: (typeData) => apiClient.post('/database-types/', typeData),
    update: (typeId, typeData) => apiClient.put(`/database-types/${typeId}/`, typeData),
    delete: (typeId) => apiClient.delete(`/database-types/${typeId}/`),
  },

  // ==================== COMPLIANCE FRAMEWORKS ====================
  complianceFrameworks: {
    getAll: () => apiClient.get('/compliance-frameworks/'),
    create: (frameworkData) => apiClient.post('/compliance-frameworks/', frameworkData),
    update: (frameworkId, frameworkData) => apiClient.put(`/compliance-frameworks/${frameworkId}/`, frameworkData),
    delete: (frameworkId) => apiClient.delete(`/compliance-frameworks/${frameworkId}/`),
  },

  // ==================== CONFIGURATION ====================
  config: {
    // Compliance Frameworks Configuration
    getComplianceFrameworks: () => apiClient.get('/api/databases/config/compliance-frameworks/'),
    getAllComplianceFrameworks: () => apiClient.get('/api/databases/config/compliance-frameworks/all/'),
    createComplianceFramework: (frameworkData) => apiClient.post('/api/databases/config/compliance-frameworks/create/', frameworkData),
    updateComplianceFramework: (frameworkName, frameworkData) => apiClient.put(`/api/databases/config/compliance-frameworks/${frameworkName}/update/`, frameworkData),
    deleteComplianceFramework: (frameworkName) => apiClient.delete(`/api/databases/config/compliance-frameworks/${frameworkName}/delete/`),

    // Database Types Configuration
    getDatabaseTypes: () => apiClient.get('/api/databases/config/database-types/'),
    getAllDatabaseTypes: () => apiClient.get('/api/databases/config/database-types/all/'),
    createDatabaseType: (typeData) => apiClient.post('/api/databases/config/database-types/create/', typeData),
    updateDatabaseType: (typeName, typeData) => apiClient.put(`/api/databases/config/database-types/${typeName}/update/`, typeData),
    deleteDatabaseType: (typeName) => apiClient.delete(`/api/databases/config/database-types/${typeName}/delete/`),
  },

  // ==================== PRIVILEGE MAPPINGS ====================
  privilegeMappings: {
    getAll: () => apiClient.get('/privilege-mappings/'),
    create: (mappingData) => apiClient.post('/privilege-mappings/', mappingData),
    update: (mappingId, mappingData) => apiClient.put(`/privilege-mappings/${mappingId}/`, mappingData),
    delete: (mappingId) => apiClient.delete(`/privilege-mappings/${mappingId}/`),
    recalculate: () => apiClient.post('/privilege-mappings/recalculate/'),
  },

  // ==================== REPORTS ====================
  reports: {
    getDashboard: () => apiClient.get('/reports/dashboard/'),
    getOracleUserAccess: (params) => apiClient.get('/reports/oracle-user-access/', { params }),
    getLateralMovement: (params) => apiClient.get('/reports/lateral-movement/', { params }),
    exportToPDF: (reportType, params) => apiClient.get(`/reports/${reportType}/pdf/`, {
      params,
      responseType: 'blob'
    }),
  },

  // ==================== DATABASE REFRESH ====================
  databaseRefresh: {
    // Legacy endpoints (kept for backward compatibility)
    getRefreshInfo: (dbType) => apiClient.get(`/refresh/${dbType}/info/`),
    startRefresh: (dbType) => apiClient.post(`/refresh/${dbType}/start/`),
    getRefreshStatus: (dbType, jobId) => apiClient.get(`/refresh/${dbType}/status/${jobId}/`),
    getRefreshHistory: (dbType) => apiClient.get(`/refresh/${dbType}/history/`),

    // V1 API deprecated - all job management now uses V2 schema below
    // ==================== V2 API (Active Normalized Schema) ====================
    v2: {
      // Job Definitions
      listDefinitions: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.is_enabled !== undefined) queryParams.append('is_enabled', params.is_enabled);
        if (params.database_type) queryParams.append('database_type', params.database_type);
        if (params.compliance_framework) queryParams.append('compliance_framework', params.compliance_framework);
        const queryString = queryParams.toString();
        return apiClient.get(`/api/databases/refresh/v2/definitions/${queryString ? '?' + queryString : ''}`);
      },
      createDefinition: (definitionData) => apiClient.post('/api/databases/refresh/v2/definitions/create/', definitionData),
      updateDefinition: (databaseType, complianceFramework, definitionData) =>
        apiClient.put(`/api/databases/refresh/v2/definitions/${databaseType}/${complianceFramework}/`, definitionData),
      deleteDefinition: (databaseType, complianceFramework) =>
        apiClient.delete(`/api/databases/refresh/v2/definitions/${databaseType}/${complianceFramework}/delete/`),

      // Quick Refresh (On-Demand Executions)
      createQuickRefresh: (refreshData) => apiClient.post('/api/databases/refresh/v2/quick-refresh/', refreshData),

      // Executions
      listExecutions: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.execution_type) queryParams.append('execution_type', params.execution_type);
        if (params.execution_status) queryParams.append('execution_status', params.execution_status);
        if (params.database_type) queryParams.append('database_type', params.database_type);
        if (params.compliance_framework) queryParams.append('compliance_framework', params.compliance_framework);
        if (params.triggered_by) queryParams.append('triggered_by', params.triggered_by);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.offset) queryParams.append('offset', params.offset);
        const queryString = queryParams.toString();
        return apiClient.get(`/api/databases/refresh/v2/executions/${queryString ? '?' + queryString : ''}`);
      },
      getExecutionDetails: (executionId) => apiClient.get(`/api/databases/refresh/v2/executions/${executionId}/`),
      getExecutionSteps: (executionId) => apiClient.get(`/api/databases/refresh/v2/executions/${executionId}/steps/`),
      getExecutionLogs: (executionId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.log_level) queryParams.append('log_level', params.log_level);
        if (params.step_order) queryParams.append('step_order', params.step_order);
        if (params.limit) queryParams.append('limit', params.limit);
        const queryString = queryParams.toString();
        return apiClient.get(`/api/databases/refresh/v2/executions/${executionId}/logs/${queryString ? '?' + queryString : ''}`);
      },
      restartExecution: (executionId) => apiClient.post(`/api/databases/refresh/v2/executions/${executionId}/restart/`),
      cleanupFailedExecutions: () => apiClient.post('/api/databases/refresh/v2/executions/cleanup-failed/'),

      // Database Selection
      getDatabasesForJob: (databaseType, complianceFramework) =>
        apiClient.get(`/api/databases/refresh/v2/databases/${databaseType}/${complianceFramework}/`),
    },
  },

  // ==================== ATTESTER MANAGEMENT ====================
  attesters: {
    getOverrides: () => apiClient.get('/attester-overrides/'),
    createOverride: (overrideData) => apiClient.post('/attester-overrides/', overrideData),
    updateOverride: (overrideId, overrideData) => apiClient.put(`/attester-overrides/${overrideId}/`, overrideData),
    deleteOverride: (overrideId) => apiClient.delete(`/attester-overrides/${overrideId}/`),

    // Attester Replacement
    replace: (replacementData) => apiClient.post('/attester-replacement/', replacementData),
    delegate: (delegationData) => apiClient.post('/attester-delegation/', delegationData),
  },

  // ==================== AI QUERY ASSISTANT ====================
  ai: {
    query: (queryData) => apiClient.post('/ai-query/', queryData),
    getHistory: () => apiClient.get('/ai-query/history/'),
  },

  // ==================== AUTHENTICATION ====================
  auth: {
    login: (credentials) => apiClient.post('/auth/login/', credentials),
    logout: () => apiClient.post('/auth/logout/'),
    getCurrentUser: () => apiClient.get('/auth/me/'),
    changePassword: (passwordData) => apiClient.post('/auth/change-password/', passwordData),
  },
};

/**
 * WHAT: Triggers a browser file download from a Blob response.
 * WHY:  Several DAMA reports (PDF exports, CSV downloads) return binary blobs that need to be
 *       saved as files on the user's machine; this helper encapsulates the temporary-link trick.
 * @param {Blob} blob - Binary data to download
 * @param {string} filename - Suggested filename for the download
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * WHAT: Retries a failed API request with exponential backoff, skipping retries on 4xx errors.
 * WHY:  Transient network or server errors (502/503) during long-running operations like
 *       database refresh should not immediately fail the UI; this gives the backend time to recover.
 * @param {Function} requestFn - Async function that performs the API call
 * @param {number} maxRetries - Maximum number of retry attempts (default 3)
 * @param {number} delay - Initial delay in ms before first retry (default 1000)
 */
export const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      // Don't retry on client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }

      // Don't retry on last attempt
      if (i === maxRetries - 1) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));

      console.log(`Retrying request (${i + 1}/${maxRetries})...`);
    }
  }
};

// Export the configured axios client as default
export default apiClient;
